import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { getElogio, updateElogio, generaTestoElogio, downloadPDFElogio, uploadGalleriaImage, deleteGalleriaImage, getGalleria, getFileUrl, creaOrdine, creaStripeCheckout, creaPayPalOrder, getPrezzi, syncElogioWix, attivaElogioGratuito } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Save, Sparkles, FileText, Loader2, Upload, X, Image as ImageIcon, CreditCard, Wallet, Euro, CheckCircle, Globe, Crop } from 'lucide-react';
import ImageCropDialog from '@/components/ImageCropDialog';

const ElogioDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isAzienda } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [syncingWix, setSyncingWix] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [activatingFree, setActivatingFree] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [prezzi, setPrezzi] = useState({ prezzo_base: 150, prezzo_premium: 100 });
  const [elogio, setElogio] = useState(null);
  const [galleria, setGalleria] = useState({ immagine_principale: null, galleria: [] });
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [cropImageId, setCropImageId] = useState(null);
  const [cropIsPrincipale, setCropIsPrincipale] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [pendingSave, setPendingSave] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    data_nascita: '',
    data_morte: '',
    biografia_raw: '',
    passioni_raw: '',
    email: '',
    biografia_extra: '',
    passioni_extra: '',
    citazioni: '',
  });

  useEffect(() => {
    loadElogio();
    loadGalleria();
    loadPrezzi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadPrezzi = async () => {
    try {
      const response = await getPrezzi();
      setPrezzi(response.data);
    } catch (error) {
      console.error('Errore caricamento prezzi:', error);
    }
  };

  const handlePayment = async (metodo) => {
    setProcessingPayment(true);
    try {
      // 1. Crea ordine
      const ordineResponse = await creaOrdine(id);
      const ordineId = ordineResponse.data.id;
      
      // 2. Crea checkout in base al metodo
      if (metodo === 'stripe') {
        const checkoutResponse = await creaStripeCheckout(ordineId);
        // Redirect a Stripe
        window.location.href = checkoutResponse.data.checkout_url;
      } else if (metodo === 'paypal') {
        const paypalResponse = await creaPayPalOrder(ordineId);
        // Redirect a PayPal
        window.location.href = paypalResponse.data.approve_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel processo di pagamento');
      setProcessingPayment(false);
    }
  };

  const handleSyncWix = async () => {
    setSyncingWix(true);
    try {
      const response = await syncElogioWix(id);
      if (response.data.success) {
        toast.success('Memorial sincronizzato online!');
        loadElogio(); // Ricarica per aggiornare lo stato wix_synced
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore sincronizzazione');
    } finally {
      setSyncingWix(false);
    }
  };

  const handleAttivaGratuito = async () => {
    if (!window.confirm('Sei sicuro di voler attivare gratuitamente questo elogio?')) return;
    setActivatingFree(true);
    try {
      const response = await attivaElogioGratuito(id);
      if (response.data.success) {
        toast.success('Elogio attivato gratuitamente!');
        loadElogio(); // Ricarica per aggiornare lo stato
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore attivazione');
    } finally {
      setActivatingFree(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!elogio) return;
    setDownloading(true);
    try {
      await downloadPDFElogio(id, elogio.nome, elogio.cognome);
    } catch (error) {
      console.error('Errore download PDF:', error);
    } finally {
      setDownloading(false);
    }
  };

  const loadElogio = async () => {
    try {
      const response = await getElogio(id);
      setElogio(response.data);
      setFormData({
        nome: response.data.nome || '',
        cognome: response.data.cognome || '',
        data_nascita: response.data.data_nascita ? response.data.data_nascita.split('T')[0] : '',
        data_morte: response.data.data_morte ? response.data.data_morte.split('T')[0] : '',
        biografia_raw: response.data.biografia_raw || '',
        passioni_raw: response.data.passioni_raw || '',
        email: response.data.email || '',
        biografia_extra: response.data.biografia_extra || '',
        passioni_extra: response.data.passioni_extra || '',
        citazioni: response.data.citazioni || '',
      });
    } catch (error) {
      toast.error('Errore nel caricamento elogio');
      navigate('/elogi');
    } finally {
      setLoading(false);
    }
  };

  const loadGalleria = async () => {
    try {
      const response = await getGalleria(id);
      setGalleria(response.data);
    } catch (error) {
      console.error('Errore caricamento galleria:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Se l'elogio è già pubblicato (qr_attivo), chiedi se vuole sincronizzare con Wix
    if (elogio?.qr_attivo) {
      setPendingSave(true);
      setPublishDialogOpen(true);
      return;
    }
    
    // Altrimenti salva normalmente
    await saveElogio();
  };

  const saveElogio = async () => {
    setSaving(true);
    try {
      await updateElogio(id, formData);
      toast.success('Elogio salvato');
      loadElogio();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
      setPendingSave(false);
    }
  };

  const handleSaveAndPublish = async () => {
    setPublishDialogOpen(false);
    setSaving(true);
    try {
      // Salva il form solo se c'è un salvataggio pendente
      if (pendingSave) {
        await updateElogio(id, formData);
        toast.success('Elogio salvato');
      }
      
      // Sincronizza con Wix
      setSyncingWix(true);
      const response = await syncElogioWix(id);
      if (response.data.success) {
        toast.success('Modifiche pubblicate online!');
      }
      loadElogio();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio/pubblicazione');
    } finally {
      setSaving(false);
      setSyncingWix(false);
      setPendingSave(false);
    }
  };

  const handleSaveOnly = async () => {
    setPublishDialogOpen(false);
    if (pendingSave) {
      await saveElogio();
    }
    // Se non c'è salvataggio pendente (popup da modifica immagini), non fare nulla
    setPendingSave(false);
  };

  const handleGeneraAI = async () => {
    setGenerating(true);
    try {
      await generaTestoElogio(id);
      toast.success('Testo generato con AI');
      loadElogio();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore generazione AI');
    } finally {
      setGenerating(false);
    }
  };

  const handleImageUpload = async (e, isPrincipale = false) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploading(true);
    try {
      // Upload multiplo per galleria, singolo per immagine principale
      const filesToUpload = isPrincipale ? [files[0]] : Array.from(files);
      
      for (const file of filesToUpload) {
        await uploadGalleriaImage(id, file, isPrincipale);
      }
      
      toast.success(isPrincipale 
        ? 'Immagine principale caricata' 
        : `${filesToUpload.length} immagine/i aggiunta/e alla galleria`
      );
      await loadGalleria();
      
      // Se l'elogio è pubblicato, chiedi se vuole aggiornare su Wix
      if (elogio?.qr_attivo && elogio?.wix_synced) {
        setPublishDialogOpen(true);
      }
    } catch (error) {
      toast.error('Errore nel caricamento immagine');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (fileId) => {
    if (!confirm('Eliminare questa immagine?')) return;
    try {
      await deleteGalleriaImage(id, fileId);
      toast.success('Immagine eliminata');
      await loadGalleria();
      
      // Se l'elogio è pubblicato, chiedi se vuole aggiornare su Wix
      if (elogio?.qr_attivo && elogio?.wix_synced) {
        setPublishDialogOpen(true);
      }
    } catch (error) {
      toast.error('Errore eliminazione immagine');
    }
  };

  const openCropDialog = (fileId, isPrincipale = false) => {
    setImageToCrop(getFileUrl(fileId));
    setCropImageId(fileId);
    setCropIsPrincipale(isPrincipale);
    setCropDialogOpen(true);
  };

  const handleSaveCroppedImage = async (croppedBlob) => {
    try {
      // Prima elimina l'immagine vecchia
      await deleteGalleriaImage(id, cropImageId);
      
      // Poi carica quella nuova ritagliata
      const file = new File([croppedBlob], 'cropped_image.jpg', { type: 'image/jpeg' });
      await uploadGalleriaImage(id, file, cropIsPrincipale);
      
      toast.success('Immagine ritagliata e salvata');
      await loadGalleria();
      
      // Se l'elogio è pubblicato, chiedi se vuole aggiornare su Wix
      if (elogio?.qr_attivo && elogio?.wix_synced) {
        setPublishDialogOpen(true);
      }
    } catch (error) {
      toast.error('Errore nel salvataggio dell\'immagine ritagliata');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!elogio) return null;

  return (
    <div data-testid="elogio-detail-page">
      {/* Header */}
      <div className="flex flex-col gap-4 mb-6 lg:mb-8">
        <div className="flex items-start gap-3">
          <Button variant="outline" size="icon" onClick={() => navigate('/elogi')} data-testid="back-btn" className="flex-shrink-0">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 
              className="text-xl sm:text-2xl lg:text-4xl font-semibold text-slate-900 tracking-tight truncate"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              {elogio.nome} {elogio.cognome}
            </h1>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <span className="text-xs sm:text-sm font-mono text-slate-500">{elogio.id}</span>
              {elogio.processed === 'YES' ? (
                <Badge className="bg-green-100 text-green-700">Elaborato</Badge>
              ) : (
                <Badge variant="secondary">Da elaborare</Badge>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          {/* Pulsante Paga e Pubblica - solo se elaborato e QR non attivo */}
          {elogio.processed === 'YES' && !elogio.qr_attivo && (
            <Button 
              size="sm" 
              onClick={() => setPaymentDialogOpen(true)}
              className="bg-green-600 hover:bg-green-700"
              data-testid="paga-pubblica-btn"
            >
              <Euro className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Paga e Pubblica</span>
              <span className="sm:hidden">Pubblica</span>
            </Button>
          )}
          {/* Pulsante Attiva Gratuito - solo admin, se elaborato e QR non attivo */}
          {isAdmin() && elogio.processed === 'YES' && !elogio.qr_attivo && (
            <Button 
              size="sm" 
              onClick={handleAttivaGratuito}
              disabled={activatingFree}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="attiva-gratuito-btn"
            >
              {activatingFree ? (
                <Loader2 className="h-4 w-4 sm:mr-2 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 sm:mr-2" />
              )}
              <span className="hidden sm:inline">Attiva Gratuito</span>
              <span className="sm:hidden">Gratis</span>
            </Button>
          )}
          {/* Pulsante Pubblica - se elaborato e QR attivo */}
          {elogio.processed === 'YES' && elogio.qr_attivo && (
            <Button 
              size="sm" 
              onClick={handleSyncWix}
              disabled={syncingWix}
              className="bg-green-600 hover:bg-green-700"
              data-testid="sync-wix-btn"
            >
              {syncingWix ? (
                <Loader2 className="h-4 w-4 sm:mr-2 animate-spin" />
              ) : (
                <Globe className="h-4 w-4 sm:mr-2" />
              )}
              <span className="hidden sm:inline">Pubblica</span>
            </Button>
          )}
          {elogio.processed === 'YES' && (
            <Button 
              size="sm" 
              onClick={handleDownloadPDF}
              disabled={downloading}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="download-pdf-btn"
            >
              {downloading ? (
                <Loader2 className="h-4 w-4 sm:mr-2 animate-spin" />
              ) : (
                <FileText className="h-4 w-4 sm:mr-2" />
              )}
              <span className="hidden sm:inline">Scarica PDF</span>
            </Button>
          )}
          <Button 
            size="sm"
            onClick={handleGeneraAI}
            disabled={generating}
            className="bg-amber-500 hover:bg-amber-600"
            data-testid="genera-ai-btn"
          >
            {generating ? (
              <Loader2 className="h-4 w-4 sm:mr-2 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 sm:mr-2" />
            )}
            <span className="hidden sm:inline">Genera con AI</span>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        {/* Form principale */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg sm:text-xl">Dati Elogio</CardTitle>
              <CardDescription className="text-sm">Modifica le informazioni del memorial</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="nome">Nome *</Label>
                    <Input
                      id="nome"
                      value={formData.nome}
                      onChange={(e) => setFormData({...formData, nome: e.target.value})}
                      required
                      data-testid="input-nome"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cognome">Cognome *</Label>
                    <Input
                      id="cognome"
                      value={formData.cognome}
                      onChange={(e) => setFormData({...formData, cognome: e.target.value})}
                      required
                      data-testid="input-cognome"
                    />
                  </div>
                  <div>
                    <Label htmlFor="data_nascita">Data Nascita</Label>
                    <Input
                      id="data_nascita"
                      type="date"
                      value={formData.data_nascita}
                      onChange={(e) => setFormData({...formData, data_nascita: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="data_morte">Data Decesso</Label>
                    <Input
                      id="data_morte"
                      type="date"
                      value={formData.data_morte}
                      onChange={(e) => setFormData({...formData, data_morte: e.target.value})}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="email">Email contatto</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h3 className="font-medium text-slate-900 mb-4 text-sm sm:text-base">Dati Originali (input)</h3>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="biografia_raw">Biografia originale</Label>
                      <Textarea
                        id="biografia_raw"
                        rows={3}
                        value={formData.biografia_raw}
                        onChange={(e) => setFormData({...formData, biografia_raw: e.target.value})}
                        placeholder="Racconta la storia di questa persona..."
                      />
                    </div>
                    <div>
                      <Label htmlFor="passioni_raw">Passioni e interessi originali</Label>
                      <Textarea
                        id="passioni_raw"
                        rows={2}
                        value={formData.passioni_raw}
                        onChange={(e) => setFormData({...formData, passioni_raw: e.target.value})}
                        placeholder="Hobby, interessi, passioni..."
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h3 className="font-medium text-slate-900 mb-1 text-sm sm:text-base flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-amber-500" />
                    Proposta Testi Elaborati (AI)
                  </h3>
                  <p className="text-xs text-slate-500 mb-4">Puoi modificare, correggere o eliminare i testi generati dall'AI e poi salvare</p>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="biografia_extra">Biografia elaborata</Label>
                      <Textarea
                        id="biografia_extra"
                        rows={4}
                        value={formData.biografia_extra}
                        onChange={(e) => setFormData({...formData, biografia_extra: e.target.value})}
                        className="bg-slate-50"
                      />
                    </div>
                    <div>
                      <Label htmlFor="passioni_extra">Passioni elaborate</Label>
                      <Textarea
                        id="passioni_extra"
                        rows={3}
                        value={formData.passioni_extra}
                        onChange={(e) => setFormData({...formData, passioni_extra: e.target.value})}
                        className="bg-slate-50"
                      />
                    </div>
                    <div>
                      <Label htmlFor="citazioni">Citazione commemorativa</Label>
                      <Textarea
                        id="citazioni"
                        rows={2}
                        value={formData.citazioni}
                        onChange={(e) => setFormData({...formData, citazioni: e.target.value})}
                        className="bg-slate-50"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <Button type="submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto" data-testid="save-btn">
                    {saving ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Salva Modifiche
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - QR e Galleria */}
        <div className="space-y-4 lg:space-y-6">
          {/* Info QR */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base sm:text-lg">Stato QR Code</CardTitle>
            </CardHeader>
            <CardContent>
              {elogio.qr_id ? (
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600 text-sm">Numero QR:</span>
                    <Badge variant="outline" className="font-mono">#{elogio.qr_id}</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600 text-sm">Stato:</span>
                    {elogio.qr_attivo ? (
                      <Badge className="bg-green-100 text-green-700">Attivo</Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-700">Non attivo</Badge>
                    )}
                  </div>
                  {elogio.link && (
                    <div className="pt-2">
                      <Label className="text-slate-600 text-sm">Link pubblico:</Label>
                      <a 
                        href={elogio.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs sm:text-sm text-blue-600 hover:underline break-all block mt-1"
                      >
                        {elogio.link}
                      </a>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-slate-500 text-sm">Nessun QR Code assegnato</p>
              )}
            </CardContent>
          </Card>

          {/* Galleria */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base sm:text-lg">Galleria Immagini</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Immagine principale */}
              <div>
                <Label className="text-slate-600 mb-2 block text-sm">Immagine Principale</Label>
                {galleria.immagine_principale ? (
                  <div className="relative group">
                    <img 
                      src={getFileUrl(galleria.immagine_principale)} 
                      alt="Principale"
                      className="w-full h-32 sm:h-40 object-cover rounded-lg"
                    />
                    <div className="absolute top-2 right-2 flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => openCropDialog(galleria.immagine_principale, true)}
                        className="p-1.5 bg-blue-500 text-white rounded-full"
                        title="Ritaglia immagine"
                      >
                        <Crop className="h-3 w-3 sm:h-4 sm:w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteImage(galleria.immagine_principale)}
                        className="p-1.5 bg-red-500 text-white rounded-full"
                        title="Elimina immagine"
                      >
                        <X className="h-3 w-3 sm:h-4 sm:w-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-28 sm:h-32 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                    <ImageIcon className="h-6 w-6 sm:h-8 sm:w-8 text-slate-400 mb-2" />
                    <span className="text-xs sm:text-sm text-slate-500">Carica immagine</span>
                    <input 
                      type="file" 
                      className="hidden" 
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, true)}
                      disabled={uploading}
                    />
                  </label>
                )}
              </div>

              {/* Galleria */}
              <div>
                <Label className="text-slate-600 mb-2 block text-sm">Altre Immagini</Label>
                <div className="grid grid-cols-3 sm:grid-cols-2 gap-2">
                  {galleria.galleria?.map((fileId) => (
                    <div key={fileId} className="relative group">
                      <img 
                        src={getFileUrl(fileId)} 
                        alt="Galleria"
                        className="w-full h-20 sm:h-24 object-cover rounded-lg"
                      />
                      <div className="absolute top-1 right-1 flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => openCropDialog(fileId, false)}
                          className="p-1 bg-blue-500 text-white rounded-full"
                          title="Ritaglia immagine"
                        >
                          <Crop className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => handleDeleteImage(fileId)}
                          className="p-1 bg-red-500 text-white rounded-full"
                          title="Elimina immagine"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                  <label className="flex flex-col items-center justify-center h-20 sm:h-24 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                    {uploading ? (
                      <Loader2 className="h-5 w-5 sm:h-6 sm:w-6 text-slate-400 animate-spin" />
                    ) : (
                      <>
                        <Upload className="h-5 w-5 sm:h-6 sm:w-6 text-slate-400" />
                        <span className="text-xs text-slate-500 mt-1">Aggiungi</span>
                      </>
                    )}
                    <input 
                      type="file" 
                      className="hidden" 
                      accept="image/*"
                      multiple
                      onChange={(e) => handleImageUpload(e, false)}
                      disabled={uploading}
                    />
                  </label>
                </div>
                <p className="text-xs text-slate-400 mt-1">Puoi selezionare più immagini contemporaneamente</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modale Pagamento */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
              Paga e Pubblica Memorial
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="font-medium text-slate-900">
                {elogio?.nome} {elogio?.cognome}
              </p>
              <p className="text-sm text-slate-500 mt-1">
                Una volta pagato, il memorial sarà pubblicato e accessibile tramite QR Code.
              </p>
              <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                <CheckCircle className="h-4 w-4" />
                L'elogio potrà essere modificato anche in seguito
              </p>
            </div>
            
            <div className="text-center py-2">
              <p className="text-sm text-slate-500">Importo da pagare</p>
              <p className="text-3xl font-bold text-slate-900">
                €{prezzi.prezzo_base || 150}
              </p>
            </div>

            <div className="space-y-3">
              <p className="text-sm font-medium text-slate-700">Scegli metodo di pagamento:</p>
              
              <Button 
                className="w-full justify-start gap-3 h-14 bg-[#635BFF] hover:bg-[#5851DB]"
                onClick={() => handlePayment('stripe')}
                disabled={processingPayment}
                data-testid="pay-stripe-btn"
              >
                {processingPayment ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <CreditCard className="h-5 w-5" />
                )}
                <div className="text-left">
                  <p className="font-medium">Carta di Credito/Debito</p>
                  <p className="text-xs opacity-80">Visa, Mastercard, Amex</p>
                </div>
              </Button>

              <Button 
                className="w-full justify-start gap-3 h-14 bg-[#0070BA] hover:bg-[#005EA6]"
                onClick={() => handlePayment('paypal')}
                disabled={processingPayment}
                data-testid="pay-paypal-btn"
              >
                {processingPayment ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Wallet className="h-5 w-5" />
                )}
                <div className="text-left">
                  <p className="font-medium">PayPal</p>
                  <p className="text-xs opacity-80">Paga con il tuo account PayPal</p>
                </div>
              </Button>
            </div>

            <p className="text-xs text-slate-500 text-center pt-2">
              Pagamento sicuro. I dati della carta non vengono salvati.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Crop Immagine */}
      <ImageCropDialog
        open={cropDialogOpen}
        onClose={() => setCropDialogOpen(false)}
        imageUrl={imageToCrop}
        onSave={handleSaveCroppedImage}
        aspectRatio={cropIsPrincipale ? 1 : undefined}
      />

      {/* Dialog Conferma Pubblicazione Wix */}
      <Dialog open={publishDialogOpen} onOpenChange={setPublishDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              <Globe className="h-5 w-5 text-blue-600" />
              Pubblicare le modifiche?
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              Questo elogio è già pubblicato online. Vuoi rendere pubblico l'aggiornamento?
            </p>
            <p className="text-sm text-slate-500 mt-2">
              Se selezioni "No", le modifiche saranno salvate solo localmente.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 justify-end">
            <Button 
              variant="outline" 
              onClick={handleSaveOnly}
              disabled={saving}
            >
              No, salva solo qui
            </Button>
            <Button 
              onClick={handleSaveAndPublish}
              disabled={saving || syncingWix}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {(saving || syncingWix) ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Globe className="h-4 w-4 mr-2" />
              )}
              Sì, rendi pubblico
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ElogioDetailPage;
